// =============================================================================
// TrackerTable.jsx — versão original restaurada
// =============================================================================

import React, { useState, useMemo } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TableSortLabel, Paper, Chip, IconButton, Typography,
  Box, Tooltip, LinearProgress, Collapse, Dialog,
} from '@mui/material';
import EditIcon          from '@mui/icons-material/Edit';
import ContentCopyIcon   from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon    from '@mui/icons-material/ExpandMore';
import FullscreenIcon      from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon  from '@mui/icons-material/FullscreenExit';
import celfinetLogo      from '../../../assets/Cyient_Slate_Std_RGB.png';

const fmtDate = (raw) => {
  if (!raw) return '—';
  const s = String(raw);
  const slash = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (slash) return `${slash[3]}-${slash[1]}-${slash[2]}`;
  const dash = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dash) {
    const [, yyyy, a, b] = dash;
    return Number(a) > 12 ? `${yyyy}-${b}-${a}` : `${yyyy}-${a}-${b}`;
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toISOString().slice(0, 10);
};

// Timestamp para ordenação — reutiliza a normalização do fmtDate (que já
// lida com "DD/MM/YYYY" e datas trocadas "YYYY-DD-MM") em vez de comparar a
// string em bruto. Sem isto, o Start/End ordenava mal porque comparava
// strings em formatos diferentes carácter a carácter, ignorando na prática
// o ano. Datas em falta/invalidas vão sempre para o fim.
const toTimestamp = (raw) => {
  if (!raw) return Infinity;
  const t = Date.parse(fmtDate(raw));
  return Number.isNaN(t) ? Infinity : t;
};

// Status mostrado ao utilizador — calculado a partir das datas, nunca
// gravado (excepto Closed, que é o único guardado explicitamente):
//   In Progress → hoje está entre StartDate e EndDate
//   Planned     → StartDate ainda está no futuro (não começou)
//   Overdue     → EndDate já passou e a atividade nunca foi fechada
//   Closed      → status = 'Closed' guardado, independente das datas
// Sem data de fim (atividade em aberto) nunca fica Overdue.
const getDisplayStatus = (entry) => {
  if ((entry.status ?? 'Open') === 'Closed') return 'Closed';

  const now   = Date.now();
  const start = toTimestamp(entry.startDate); // Infinity se em falta/inválida
  const end   = toTimestamp(entry.endDate);   // Infinity se em falta/inválida

  if (end < now) return 'Overdue';
  if (start !== Infinity && start > now) return 'Planned';
  return 'In Progress';
};

// Cores por status — Closed mantém a cor que já tinha (cinzento neutro).
const STATUS_COLORS = {
  'In Progress': { bg: '#3eac36', fg: '#000000' },
  'Planned':     { bg: '#2196F3', fg: '#FFFFFF' },
  'Overdue':     { bg: '#FFBF00', fg: '#000000' },
  'Closed':      { bg: 'grey.200', fg: 'text.secondary' },
};

// Mesmo mapeamento de negócio do filtro Side na barra principal
// (TrackerPage.jsx / TrackerFilters.jsx): West → TFinVF; East → TFinTF e
// VFinTF. Aqui na tabela mostra-se East/West (não o valor cru) para ficar
// consistente com o que já se vê no filtro.
const SIDE_BUCKET = { TFinTF: 'East', VFinTF: 'East', TFinVF: 'West' };

// Buckets (East/West) presentes na atividade, distintos e ordenados, a
// partir dos sites já gravados (server.tacs[].siteSides). Uma atividade
// pode ter mais que um (um único OMC_ID pode ter sites com Side diferente
// entre si — confirmado por SQL), por isso devolve sempre um array.
const getEntrySides = (entry) => {
  const buckets = new Set();
  (entry.servers ?? []).forEach((s) =>
    (s.tacs ?? []).forEach((t) =>
      Object.values(t.siteSides ?? {}).forEach((side) => {
        const bucket = SIDE_BUCKET[side];
        if (bucket) buckets.add(bucket);
      })
    )
  );
  return [...buckets].sort();
};

const COLUMNS = [
  { id: 'title',       label: 'Activity', sortKey: (e) => e.title?.toLowerCase() ?? '' },
  { id: 'teamName',    label: 'Team',     sortKey: (e) => e.teamName?.toLowerCase() ?? '' },
  { id: 'side',        label: 'Side',     sortKey: (e) => getEntrySides(e).join(',').toLowerCase() },
  { id: 'technology',  label: 'Tech',     sortKey: (e) => e.technology?.toLowerCase() ?? '' },
  { id: 'vendor',      label: 'Vendor',   sortKey: (e) => e.vendor?.toLowerCase() ?? '' },
  { id: 'feature',     label: 'Type',     sortKey: (e) => e.feature?.toLowerCase() ?? '' },
  { id: 'status',      label: 'Status',   sortKey: (e) => getDisplayStatus(e).toLowerCase() },
  { id: 'startDate',   label: 'Start',    sortKey: (e) => toTimestamp(e.startDate) },
  { id: 'endDate',     label: 'End',      sortKey: (e) => toTimestamp(e.endDate) },
  { id: 'ownerNames',  label: 'Owner',    sortKey: (e) => (e.ownerNames ?? []).join(', ').toLowerCase() },
  { id: '_actions',    label: '',         sortKey: null },
];

const headerCellSx = {
  fontWeight: 700, fontSize: '0.68rem',
  textTransform: 'uppercase', letterSpacing: 0.8,
  color: 'rgba(255,255,255,0.85)', whiteSpace: 'nowrap',
  borderBottom: 'none',
};

const TrackerTable = ({ entries, loading, onRowClick, onDelete, onCopy, showClosed = false, canEditEntry = () => true, canDeleteEntry = () => true, userRole = null, userId = null, maximizedHeader = null }) => {
  const [page,        setPage]        = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortCol,     setSortCol]     = useState('startDate');
  const [sortDir,     setSortDir]     = useState('asc');
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [isMaximized, setIsMaximized] = useState(false);

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSort = (colId) => {
    if (sortCol === colId) {
      setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(colId);
      setSortDir('asc');
    }
    setPage(0);
  };

  const displayed = useMemo(() => {
    const filtered = showClosed
      ? entries
      : entries.filter((e) => (e.status ?? 'Open') !== 'Closed');
    const col = COLUMNS.find((c) => c.id === sortCol);
    if (!col?.sortKey) return filtered;
    return [...filtered].sort((a, b) => {
      const av = col.sortKey(a);
      const bv = col.sortKey(b);
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ?  1 : -1;
      return 0;
    });
  }, [entries, showClosed, sortCol, sortDir]);

  const start     = page * rowsPerPage;
  const paginated = rowsPerPage === -1 ? displayed : displayed.slice(start, start + rowsPerPage);

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const Wrapper = isMaximized ? Dialog : Paper;
  const wrapperProps = isMaximized
    ? { fullScreen: true, open: true, onClose: () => setIsMaximized(false),
        PaperProps: { sx: { position: 'relative', p: 2, display: 'flex', flexDirection: 'column', overflow: 'hidden' } } }
    : { variant: 'outlined', sx: { borderRadius: 2, overflow: 'hidden', position: 'relative' } };

  return (
    <Wrapper {...wrapperProps}>
      {isMaximized ? (
        <>
          {/* Em modo fullscreen o botão fica alinhado no fluxo normal (não
              sobreposto), porque o cabeçalho/filtros da página (maximizedHeader)
              ocupam essa zona. */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, flexShrink: 0 }}>
            <Tooltip title="Restore table">
              <IconButton size="small" onClick={() => setIsMaximized(false)}>
                <FullscreenExitIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {maximizedHeader && (
            <Box sx={{ mb: 1, flexShrink: 0 }}>
              {maximizedHeader}
            </Box>
          )}
        </>
      ) : (
        <Tooltip title="Maximize table">
          <IconButton
            size="small"
            onClick={() => setIsMaximized(true)}
            sx={{
              position: 'absolute', top: 8, right: 8, zIndex: 10,
              bgcolor: 'background.paper', boxShadow: 1,
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <FullscreenIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {loading && <LinearProgress sx={{ height: 2 }} />}
      {!loading && entries.length === 0 ? (
        <Box sx={{ p: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No activities yet. Click <strong>+ New Activity</strong> to start.
          </Typography>
        </Box>
      ) : (
        <>
          {/* Wrapper à parte da área com scroll — a marca d'água fica fora do
              conteúdo que faz scroll, senão com "Rows: All" (muitas linhas)
              ela só aparecia uma vez a meio de toda a lista em vez de ficar
              sempre centrada na zona visível enquanto se faz scroll. */}
          <Box sx={{
            position: 'relative',
            ...(isMaximized ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : {}),
          }}>
            {/* Marca d'água — igual à do Gantt Chart */}
            <Box sx={{
              position: 'absolute',
              top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none', zIndex: 0,
              width: '30%', minWidth: '200px', opacity: 0.05,
              display: 'flex', justifyContent: 'center'
            }}>
              <Box component="img" src={celfinetLogo} alt="" sx={{ width: '100%' }} />
            </Box>
            <TableContainer sx={isMaximized
              ? { flex: 1, minHeight: 0, overflow: 'auto' }
              : { maxHeight: 520 }
            }>
            <Table size="small">
              <TableHead sx={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ ...headerCellSx, width: 40, padding: 0 }} />
                  {COLUMNS.map((col) => (
                    <TableCell key={col.id} sx={headerCellSx}>
                      {col.sortKey ? (
                        <TableSortLabel
                          active={sortCol === col.id}
                          direction={sortCol === col.id ? sortDir : 'asc'}
                          onClick={() => handleSort(col.id)}
                          sx={{ fontSize: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit', fontWeight: 'inherit', color: 'inherit', '& .MuiTableSortLabel-icon': { color: 'rgba(255,255,255,0.6) !important' }, '&.Mui-active': { color: 'white' } }}
                        >
                          {col.label}
                        </TableSortLabel>
                      ) : col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginated.map((entry) => {
                  const isClosed = (entry.status ?? 'Open') === 'Closed';
                  const displayStatus = getDisplayStatus(entry);
                  const hasComments = !!entry.comments?.trim();
                  const isExpanded = expandedIds.has(entry.id);
                  return (
                    <React.Fragment key={entry.id}>
                    <TableRow
                      hover
                      onClick={() => onRowClick(entry)}
                      sx={{
                        cursor: 'pointer',
                        '& td': { borderBottom: isExpanded ? 'none' : undefined },
                        opacity: isClosed ? 0.55 : 1,
                      }}
                    >
                      <TableCell sx={{ padding: 0, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                        {hasComments && (
                          <Tooltip title={isExpanded ? 'Hide comments' : 'Show comments'}>
                            <IconButton size="small" onClick={() => toggleExpanded(entry.id)}>
                              <ExpandMoreIcon fontSize="small"
                                sx={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Tooltip title={entry.title} placement="top" enterDelay={400}>
                          <Typography variant="body2" fontWeight={600} noWrap
                            sx={{ color: isClosed ? 'text.disabled' : 'inherit' }}>
                            {entry.title}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                        <Tooltip title={entry.teamName || ''} placement="top" enterDelay={400} disableHoverListener={!entry.teamName}>
                          <span>{entry.teamName || '—'}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {(() => {
                          const sides = getEntrySides(entry);
                          if (sides.length === 0) return <Typography variant="caption" color="text.disabled">—</Typography>;
                          return (
                            <Box sx={{ display: 'flex', gap: 0.4, flexWrap: 'wrap' }}>
                              {sides.map((s) => (
                                <Chip key={s} label={s} size="small" variant="outlined"
                                  sx={{ height: 18, fontSize: '0.62rem' }} />
                              ))}
                            </Box>
                          );
                        })()}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                        {entry.technology || '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                        {entry.vendor || '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                        <Tooltip title={entry.feature || ''} placement="top" enterDelay={400} disableHoverListener={!entry.feature}>
                          <span>{entry.feature || '—'}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Chip
                          label={displayStatus}
                          size="small"
                          sx={{
                            fontSize: '0.68rem', height: 20, fontWeight: 600,
                            bgcolor: STATUS_COLORS[displayStatus]?.bg ?? 'grey.200',
                            color:   STATUS_COLORS[displayStatus]?.fg ?? 'text.secondary',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        {fmtDate(entry.startDate)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        {fmtDate(entry.endDate)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                        {(() => {
                          const owners = (entry.ownerNames?.length ? entry.ownerNames.join(', ') : entry.ownerName) || '—';
                          return (
                            <Tooltip title={owners} placement="top" enterDelay={400} disableHoverListener={owners === '—'}>
                              <span>{owners}</span>
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                        {canEditEntry(entry) && (
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => onRowClick(entry)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {onCopy && (
                          <Tooltip title="Copy activity">
                            <IconButton size="small" onClick={() => onCopy(entry)}>
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canDeleteEntry(entry) && (
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => onDelete(entry.id)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ opacity: isClosed ? 0.55 : 1 }}>
                      <TableCell sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }} colSpan={COLUMNS.length + 1}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 1.5, px: 2, bgcolor: 'action.hover', borderRadius: 1, my: 1 }}>
                            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                              Comments
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {entry.comments}
                            </Typography>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
            </TableContainer>
          </Box>
          <TablePagination
            component="div"
            count={displayed.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50, { label: 'All', value: -1 }]}
            labelRowsPerPage="Rows:"
            sx={{ borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}
          />
        </>
      )}
    </Wrapper>
  );
};

export default TrackerTable;